import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "@veritrellis/ui";
import type { ReactNode } from "react";
import type { ConnectorCatalogEntry, ConnectorPlatform } from "@veritrellis/shared/connectors";
import { connectorCatalog } from "@veritrellis/shared/connectors";
import { ConnectorBrandTile } from "./ConnectorBrandTile.js";

type ModalStep = "catalog" | "setup";

export const ConnectorCatalogModal = (input: {
  opened: boolean;
  step: ModalStep;
  catalogEntry: ConnectorCatalogEntry | null;
  /** Platforms the workspace is allowed to install. All platforms shown when undefined. */
  enabledPlatforms?: ConnectorPlatform[];
  onClose: () => void;
  onPickPlatform: (platform: ConnectorPlatform) => void;
  onBackToCatalog: () => void;
  setupBody: ReactNode;
  setupActions: ReactNode;
}) => {
  const title =
    input.step === "catalog"
      ? "Add connector"
      : input.catalogEntry !== null
        ? `Configure ${input.catalogEntry.displayName}`
        : "Configure connector";

  return (
    <Dialog open={input.opened} onOpenChange={(open) => !open && input.onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {input.step === "catalog" ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Choose a platform. You can create multiple installations (for example sandbox and
              production).
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {connectorCatalog.map((entry) => {
                const isAvailable =
                  input.enabledPlatforms === undefined ||
                  input.enabledPlatforms.includes(entry.platform);
                return isAvailable ? (
                  <ConnectorBrandTile
                    key={entry.platform}
                    iconKey={entry.iconKey}
                    displayName={entry.displayName}
                    description={entry.description}
                    onClick={() => {
                      input.onPickPlatform(entry.platform);
                    }}
                  />
                ) : (
                  <div
                    key={entry.platform}
                    className="flex flex-col gap-2 rounded-lg border border-border p-4 opacity-40"
                    title="Not available on your plan"
                  >
                    <p className="text-sm font-medium">{entry.displayName}</p>
                    <p className="text-xs text-muted-foreground">Not available on your plan</p>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={input.onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {input.setupBody}
            <div className="flex justify-between">
              <Button variant="outline" onClick={input.onBackToCatalog}>
                Back
              </Button>
              {input.setupActions}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
